import React from "react";
import { AlertOctagon, AlertTriangle, ArrowRight, Calendar, Car, ClipboardCheck, Clock, Droplet, Gauge, Package, Wallet, Wrench} from "lucide-react";
import { api } from "../../lib/api";
import { fmtRoDate } from "../../lib/dateFmt";
import { toast } from "sonner";
import TasksAdminPanel from "../../components/TasksAdminPanel";
import VideoExplicativeCard from "../../components/VideoExplicativeCard";

export default function AlertsPanel({ alerts, navigate }) {
  const oilLow = alerts.oil?.low;
  const oilLiters = alerts.oil?.total_liters || 0;
  const oilThreshold = alerts.oil?.threshold || 5;
  const lowStock = alerts.low_stock || [];
  const defectCars = alerts.defect_cars || [];
  const defectEq = alerts.defect_equipment || [];
  const mfkExp = alerts.mfk_expired || [];
  const revDue = alerts.revizie_due || [];
  const missingRev = alerts.cars_missing_revizie || [];
  const bilaR = alerts.bila_rosie || [];
  const p90 = alerts.person_90 || [];
  const pendVac = alerts.pending_vacations || [];
  const pendSal = alerts.pending_salary || [];
  const total = (oilLow ? 1 : 0) + (lowStock.length ? 1 : 0) + (defectCars.length ? 1 : 0) + (defectEq.length ? 1 : 0)
    + (mfkExp.length ? 1 : 0) + (revDue.length ? 1 : 0) + (missingRev.length ? 1 : 0) + (bilaR.length ? 1 : 0) + (p90.length ? 1 : 0)
    + (pendVac.length ? 1 : 0) + (pendSal.length ? 1 : 0);

  if (total === 0) {
    return (
      <div className="card-flat p-5 mb-10 flex items-center gap-3 border-l-4 border-l-[var(--green-valid)]" data-testid="alerts-panel-clean">
        <div className="w-10 h-10 rounded-md bg-[var(--green-bg)] grid place-items-center text-[var(--green-valid)]">✓</div>
        <div>
          <div className="font-display text-lg font-bold">Toate sistemele sunt OK</div>
          <div className="text-sm text-zinc-500">Niciun stoc critic, niciun echipament defect, niciun autoturism defect.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10" data-testid="alerts-panel">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={18} className="text-[var(--orange-bila)]"/>
        <h2 className="font-display text-xl font-bold">Alerte operaționale</h2>
        <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--orange-bila-bg)] text-[var(--orange-bila)] font-bold">{total}</span>
        <button
          onClick={async () => {
            try {
              const { data } = await api.post("/email/critical-alerts");
              if (data.skipped) toast.info(data.reason || "Nicio alertă critică");
              else toast.success(`${data.count} alerte trimise pe email la ${data.to}`);
            } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la trimitere"); }
          }}
          data-testid="alerts-email-btn"
          className="ml-auto text-xs px-3 py-1.5 rounded-md border border-[var(--border)] hover:border-[var(--red-bila)] text-zinc-600 hover:text-[var(--red-bila)] inline-flex items-center gap-1.5"
          title="Trimite alertele critice pe email"
        >
          📧 Trimite pe email
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {oilLow && (
          <button onClick={() => navigate("/inventar")} data-testid="alert-oil-low" className="card-flat p-5 text-left hover:border-[#D97706] transition-colors group border-l-4 border-l-[#D97706]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[#FEF3C7] grid place-items-center text-[#D97706]"><Droplet size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[#D97706]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[#D97706]">Stoc ulei sub prag</div>
              <div className="font-display text-2xl font-extrabold mt-1">{oilLiters.toFixed(2)} L</div>
              <div className="text-xs text-zinc-500 mt-0.5">Prag minim: {oilThreshold} L</div>
            </div>
          </button>
        )}
        {lowStock.length > 0 && (
          <button onClick={() => navigate("/inventar")} data-testid="alert-low-stock" className="card-flat p-5 text-left hover:border-[var(--orange-bila)] transition-colors group border-l-4 border-l-[var(--orange-bila)]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[var(--orange-bila-bg)] grid place-items-center text-[var(--orange-bila)]"><Package size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[var(--orange-bila)]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[var(--orange-bila)]">Piese / consumabile cu stoc minim</div>
              <div className="font-display text-2xl font-extrabold mt-1">{lowStock.length}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{lowStock.slice(0,2).map(i => i.name).join(", ")}{lowStock.length > 2 ? `, +${lowStock.length-2}` : ""}</div>
            </div>
          </button>
        )}
        {defectCars.length > 0 && (
          <div data-testid="alert-defect-cars-block" className="card-flat p-5 border-l-4 border-l-[var(--red-bila)]">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-md bg-[var(--red-bila-bg)] grid place-items-center text-[var(--red-bila)]"><Car size={20}/></div>
                <div>
                  <div className="small-label text-[var(--red-bila)]">Autoturisme defecte</div>
                  <div className="font-display text-2xl font-extrabold leading-tight">{defectCars.length}</div>
                </div>
              </div>
            </div>
            <div className="space-y-2 max-h-[180px] overflow-y-auto">
              {defectCars.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/autoturisme/${c.id}`)}
                  className="w-full text-left rounded border border-red-200 bg-red-50 p-2 hover:bg-red-100 transition-colors"
                  data-testid={`defect-car-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-bold text-red-900 truncate">
                      {c.intern_number ? `Auto ${c.intern_number} · ` : ""}{c.numar_inmatriculare || `${c.marca || ""} ${c.model || ""}`.trim() || "—"}
                    </div>
                    <ArrowRight size={12} className="text-red-400 shrink-0"/>
                  </div>
                  {c.defect_reason && (
                    <div className="text-[11px] text-red-700 mt-1 italic line-clamp-2">{c.defect_reason}</div>
                  )}
                  {c.defect_marked_by && (
                    <div className="text-[10px] text-red-500 mt-0.5">
                      Marcat de {c.defect_marked_by}{c.defect_marked_at ? ` · ${fmtRoDate(c.defect_marked_at)}` : ""}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        {defectEq.length > 0 && (
          <button onClick={() => navigate("/inventar")} data-testid="alert-defect-eq" className="card-flat p-5 text-left hover:border-[var(--red-bila)] transition-colors group border-l-4 border-l-[var(--red-bila)]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[var(--red-bila-bg)] grid place-items-center text-[var(--red-bila)]"><Wrench size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[var(--red-bila)]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[var(--red-bila)]">Echipamente defecte</div>
              <div className="font-display text-2xl font-extrabold mt-1">{defectEq.length}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{defectEq.slice(0,2).map(e => e.name).join(", ")}{defectEq.length > 2 ? `, +${defectEq.length-2}` : ""}</div>
            </div>
          </button>
        )}
        {mfkExp.length > 0 && (
          <button onClick={() => navigate("/autoturisme")} data-testid="alert-mfk" className="card-flat p-5 text-left hover:border-[var(--red-bila)] transition-colors group border-l-4 border-l-[var(--red-bila)]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[var(--red-bila-bg)] grid place-items-center text-[var(--red-bila)]"><Calendar size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[var(--red-bila)]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[var(--red-bila)]">MFK expirat</div>
              <div className="font-display text-2xl font-extrabold mt-1">{mfkExp.length}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{mfkExp.slice(0,2).map(e => e.label).join(", ")}{mfkExp.length > 2 ? `, +${mfkExp.length-2}` : ""}</div>
            </div>
          </button>
        )}
        {revDue.length > 0 && (
          <button onClick={() => navigate("/autoturisme")} data-testid="alert-revizie" className="card-flat p-5 text-left hover:border-[var(--orange-bila)] transition-colors group border-l-4 border-l-[var(--orange-bila)]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[var(--orange-bila-bg)] grid place-items-center text-[var(--orange-bila)]"><Gauge size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[var(--orange-bila)]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[var(--orange-bila)]">Revizie scadentă</div>
              <div className="font-display text-2xl font-extrabold mt-1">{revDue.length}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{revDue.slice(0,2).map(e => e.label).join(", ")}{revDue.length > 2 ? `, +${revDue.length-2}` : ""}</div>
            </div>
          </button>
        )}
        {missingRev.length > 0 && (
          <div
            data-testid="alert-missing-revizie"
            className="card-flat p-5 border-2 border-red-600 bg-red-50 animate-pulse-red-soft"
            style={{ boxShadow: "0 0 0 0 rgba(239, 68, 68, 0.5)" }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-md bg-red-600 grid place-items-center text-white animate-pulse">
                  <Gauge size={20}/>
                </div>
                <div>
                  <div className="small-label text-red-700 font-bold">Autoturisme fără dată ultimă revizie</div>
                  <div className="font-display text-2xl font-extrabold leading-tight text-red-700">{missingRev.length}</div>
                </div>
              </div>
            </div>
            <div className="space-y-2 max-h-[180px] overflow-y-auto">
              {missingRev.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/autoturisme/${c.id}`)}
                  className="w-full text-left rounded border border-red-300 bg-white p-2 hover:bg-red-100 transition-colors"
                  data-testid={`missing-revizie-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-bold text-red-900 truncate">
                      {c.intern_number ? `Auto ${c.intern_number} · ` : ""}{c.numar_inmatriculare || c.label || "—"}
                    </div>
                    <ArrowRight size={12} className="text-red-400 shrink-0"/>
                  </div>
                  {c.label && c.numar_inmatriculare && (
                    <div className="text-[11px] text-red-700 mt-0.5 italic">{c.label}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        {bilaR.length > 0 && (
          <button onClick={() => navigate("/personal")} data-testid="alert-bila-rosie" className="card-flat p-5 text-left hover:border-[var(--red-bila)] transition-colors group border-l-4 border-l-[var(--red-bila)]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[var(--red-bila-bg)] grid place-items-center text-[var(--red-bila)]"><AlertOctagon size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[var(--red-bila)]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[var(--red-bila)]">Bilă Roșie</div>
              <div className="font-display text-2xl font-extrabold mt-1">{bilaR.length}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{bilaR.slice(0,2).map(e => e.label).join(", ")}{bilaR.length > 2 ? `, +${bilaR.length-2}` : ""}</div>
            </div>
          </button>
        )}
        {p90.length > 0 && (
          <button onClick={() => navigate("/personal")} data-testid="alert-90zile" className="card-flat p-5 text-left hover:border-[var(--orange-bila)] transition-colors group border-l-4 border-l-[var(--orange-bila)]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[var(--orange-bila-bg)] grid place-items-center text-[var(--orange-bila)]"><Clock size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[var(--orange-bila)]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[var(--orange-bila)]">90 zile angajare</div>
              <div className="font-display text-2xl font-extrabold mt-1">{p90.length}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">{p90.slice(0,2).map(e => e.label).join(", ")}{p90.length > 2 ? `, +${p90.length-2}` : ""}</div>
            </div>
          </button>
        )}
        {pendVac.length > 0 && (
          <button onClick={() => navigate("/concediu")} data-testid="alert-pending-vacations" className="card-flat p-5 text-left hover:border-[#7C3AED] transition-colors group border-l-4 border-l-[#7C3AED]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[#EDE9FE] grid place-items-center text-[#7C3AED]"><ClipboardCheck size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[#7C3AED]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[#7C3AED]">Cereri concediu de aprobat</div>
              <div className="font-display text-2xl font-extrabold mt-1">{pendVac.length}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">
                {pendVac.slice(0,2).map(v => `${v.label} (${fmtRoDate(v.start_date)}→${fmtRoDate(v.end_date)})`).join(", ")}
                {pendVac.length > 2 ? `, +${pendVac.length-2}` : ""}
              </div>
            </div>
          </button>
        )}
        {pendSal.length > 0 && (
          <button onClick={() => navigate("/avansuri")} data-testid="alert-pending-salary" className="card-flat p-5 text-left hover:border-[#7C3AED] transition-colors group border-l-4 border-l-[#7C3AED]">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[#EDE9FE] grid place-items-center text-[#7C3AED]"><Wallet size={20}/></div>
              <ArrowRight size={16} className="text-zinc-400 group-hover:text-[#7C3AED]"/>
            </div>
            <div className="mt-4">
              <div className="small-label text-[#7C3AED]">Cheltuieli/avansuri de aprobat</div>
              <div className="font-display text-2xl font-extrabold mt-1">{pendSal.length}</div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">
                {pendSal.slice(0,2).map(s => `${s.label} · ${s.subcategory_code} · ${Number(s.amount || 0).toFixed(2)} CHF`).join(", ")}
                {pendSal.length > 2 ? `, +${pendSal.length-2}` : ""}
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="mt-6">
        <TasksAdminPanel />
      </div>
      <div className="mt-6">
        <VideoExplicativeCard />
      </div>
    </div>
  );
}
